import React from 'react';
import { Alert, Box, Button, CircularProgress, Dialog, Typography } from '@mui/material';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';

export const PROFILE_PROCESSING_STEPS = [
  { id: 'sources', label: 'Processing sources' },
  { id: 'cards', label: 'Generating profile cards' },
  { id: 'finalizing', label: 'Finalizing' },
];

export const processingStepIndex = (generationStep) => {
  const index = PROFILE_PROCESSING_STEPS.findIndex((step) => step.id === generationStep);
  return index === -1 ? 0 : index;
};

const stepState = (index, activeIndex, allComplete) => {
  if (allComplete || index < activeIndex) return 'complete';
  if (index === activeIndex) return 'active';
  return 'pending';
};

const CompleteMark = () => (
  <Box
    width={20}
    height={20}
    borderRadius='50%'
    bgcolor='#16a34a'
    display='flex'
    alignItems='center'
    justifyContent='center'
    flexShrink={0}
  >
    <Box component='svg' width={12} height={12} viewBox='0 0 12 12' fill='none' aria-hidden>
      <path
        d='M2.4 6.15 4.7 8.45 9.6 3.55'
        stroke='#fff'
        strokeWidth='1.7'
        strokeLinecap='round'
        strokeLinejoin='round'
      />
    </Box>
  </Box>
);

const PendingMark = () => (
  <Box width={20} height={20} borderRadius='50%' border='1.5px solid #D0D5DD' boxSizing='border-box' flexShrink={0} />
);

const StepIcon = ({ state }) => {
  if (state === 'complete') return <CompleteMark />;
  if (state === 'active') {
    return <CircularProgress size={18} thickness={4} sx={{ color: 'primary.main' }} />;
  }
  return <PendingMark />;
};

const STATUS_LABEL = {
  complete: 'Completed',
  active: 'In progress',
  pending: 'Pending',
};

const STATUS_COLOR = {
  complete: '#98A2B3',
  active: 'primary.main',
  pending: '#D0D5DD',
};

const ProcessingBar = ({ complete }) => (
  <Box
    role='progressbar'
    aria-label='Company Profile processing'
    aria-valuetext={complete ? 'Finished' : 'Processing'}
    sx={{
      mt: 2,
      height: 6,
      borderRadius: 999,
      bgcolor: '#E7EEF8',
      overflow: 'hidden',
      position: 'relative',
      '@keyframes profileProcessingBar': {
        '0%': { transform: 'translateX(-130%)' },
        '100%': { transform: 'translateX(280%)' },
      },
    }}
  >
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        bottom: 0,
        left: 0,
        width: complete ? '100%' : '42%',
        borderRadius: 999,
        bgcolor: 'primary.main',
        animation: complete ? 'none' : 'profileProcessingBar 1.35s ease-in-out infinite',
      }}
    />
  </Box>
);

const ProcessingCompanyProfileModal = ({
  open,
  generationStep,
  allComplete = false,
  error = '',
  isRetrying = false,
  onRetry,
  onClose,
}) => {
  const activeIndex = processingStepIndex(generationStep);

  return (
    <Dialog
      open={open}
      onClose={error ? onClose : undefined}
      disableEscapeKeyDown={!error}
      maxWidth={false}
      aria-labelledby='processing-company-profile-title'
      BackdropProps={{
        sx: { backgroundColor: 'rgba(15, 23, 42, 0.46)' },
      }}
      PaperProps={{
        sx: {
          width: 'min(440px, calc(100% - 64px))',
          borderRadius: '12px',
          boxShadow: '0 18px 50px rgba(15, 23, 42, 0.22)',
          p: '24px',
        },
      }}
    >
      <Box display='flex' alignItems='flex-start' gap={1.5}>
        <Box
          width={36}
          height={36}
          borderRadius='50%'
          display='flex'
          alignItems='center'
          justifyContent='center'
          flexShrink={0}
          bgcolor={error ? '#FEF2F2' : '#E8F1FC'}
        >
          {error ? (
            <ErrorOutlineIcon sx={{ fontSize: 20, color: 'error.main' }} />
          ) : (
            <Box
              component='svg'
              width={18}
              height={18}
              viewBox='0 0 24 24'
              fill='none'
              aria-hidden
              sx={{ color: 'primary.main' }}
            >
              <path d='M20 12a8 8 0 1 1-2.3-5.6' stroke='currentColor' strokeWidth='2' strokeLinecap='round' />
              <path
                d='M20 4.5V9h-4.5'
                stroke='currentColor'
                strokeWidth='2'
                strokeLinecap='round'
                strokeLinejoin='round'
              />
            </Box>
          )}
        </Box>
        <Box minWidth={0} pt={0.15}>
          <Typography
            id='processing-company-profile-title'
            sx={{ fontSize: 16, fontWeight: 700, lineHeight: 1.3, color: '#101828' }}
          >
            Processing Company Profile
          </Typography>
          {!error && (
            <Typography sx={{ mt: 0.5, fontSize: 13.5, lineHeight: 1.45, color: '#667085' }}>
              Please wait while TechXpander processes your sources and generates your Company Profile.
            </Typography>
          )}
        </Box>
      </Box>

      {error ? (
        <Box mt={2}>
          <Alert severity='error' sx={{ alignItems: 'center', '& .MuiAlert-message': { py: 0.25 } }}>
            {error}
          </Alert>
          <Box display='flex' justifyContent='flex-end' gap={1} mt={2}>
            <Button onClick={onClose} disabled={isRetrying} sx={{ textTransform: 'none', color: '#475467' }}>
              Close
            </Button>
            <Button
              variant='contained'
              onClick={onRetry}
              disabled={isRetrying}
              sx={{ textTransform: 'none', fontWeight: 600, boxShadow: 'none', borderRadius: 1.5 }}
            >
              {isRetrying ? 'Retrying…' : 'Try again'}
            </Button>
          </Box>
        </Box>
      ) : (
        <>
          <ProcessingBar complete={allComplete} />
          <Box mt={2} border='1px solid #EAECF0' borderRadius='10px' overflow='hidden'>
            {PROFILE_PROCESSING_STEPS.map((step, index) => {
              const state = stepState(index, activeIndex, allComplete);
              return (
                <Box
                  key={step.id}
                  display='flex'
                  alignItems='center'
                  justifyContent='space-between'
                  gap={2}
                  px={1.75}
                  py={1.35}
                  borderTop={index === 0 ? 0 : '1px solid #EAECF0'}
                >
                  <Box display='flex' alignItems='center' gap={1.25} minWidth={0}>
                    <Box
                      width={20}
                      height={20}
                      display='flex'
                      alignItems='center'
                      justifyContent='center'
                      flexShrink={0}
                    >
                      <StepIcon state={state} />
                    </Box>
                    <Typography
                      noWrap
                      sx={{
                        fontSize: 14,
                        fontWeight: 500,
                        color: state === 'pending' ? '#98A2B3' : '#101828',
                      }}
                    >
                      {step.label}
                    </Typography>
                  </Box>
                  <Typography sx={{ fontSize: 13.5, fontWeight: 500, color: STATUS_COLOR[state], flexShrink: 0 }}>
                    {STATUS_LABEL[state]}
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </>
      )}
    </Dialog>
  );
};

export default ProcessingCompanyProfileModal;
